import { useSearchParams } from 'react-router-dom';
import { useCallback, useMemo } from 'react';

const DEFAULTS = {
  page: 1,
  limit: 10,
};

export function useListQueryParams<T extends Record<string, any>>(
  defaultFilters?: Partial<T>,
) {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo(() => {
    const query: any = {
      page: Number(searchParams.get('page')) || DEFAULTS.page,
      limit: Number(searchParams.get('limit')) || DEFAULTS.limit,
    };

    searchParams.forEach((value, key) => {
      if (key !== 'page' && key !== 'limit') {
        query[key] = value;
      }
    });

    return {
      ...defaultFilters,
      ...query,
    } as T & typeof DEFAULTS;
  }, [searchParams, defaultFilters]);

  const setParams = useCallback(
    (newParams: Partial<T & typeof DEFAULTS>) => {
      const updated = new URLSearchParams(searchParams);

      Object.entries(newParams).forEach(([key, value]) => {
        if (
          value === undefined ||
          value === null ||
          value === '' ||
          value === 'all'
        ) {
          updated.delete(key);
        } else {
          updated.set(key, String(value));
        }
      });

      // Reset page when filters change
      if (Object.keys(newParams).some((k) => k !== 'page' && k !== 'limit')) {
        updated.set('page', '1');
      }

      setSearchParams(updated);
    },
    [searchParams, setSearchParams],
  );

  return {
    params,
    setParams,
  };
}
